import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const debounceCode = `// Engineering Precision: Debouncing (The "Wait Until Silent" Pattern)
function debounce(fn, ms) {
    let timeoutId;
    return (...args) => {
        // Reset the timer on every call
        clearTimeout(timeoutId); 
        timeoutId = setTimeout(() => fn.apply(this, args), ms);
    };
}

// Production Usage: Search entry with 300ms "cooldown"
const performSearch = debounce((query) => {
    console.log("Network Request for Hub Search:", query);
}, 300);`;

const throttleCode = `// Engineering Precision: Throttling (The "Fixed Interval" Pattern)
function throttle(fn, ms) {
    let lastCall = 0;
    return (...args) => {
        const now = Date.now();
        if (now - lastCall >= ms) {
            lastCall = now;
            fn.apply(this, args);
        }
    };
}

// Production Usage: Scroll handler capped at 60fps (approx 16ms)
const updateScrollUI = throttle(() => {
    const depth = window.scrollY;
    console.log("Updating Parallax depth:", depth);
}, 16);`;

const visualizationCode = `// The Mechanical Difference:
// Scenario: User types 5 characters in 1 second.

// [ Debounce ]
// User: T---Y---P---I---N---G (stop)
// Callback: -----------------(Result)
// Logic: Waits for the *pause* before executing once.

// [ Throttle ]
// User: T---Y---P---I---N---G (stop)
// Callback: T-------P-------N
// Logic: Executes at regular *intervals*, regardless of pause.`;

const JSDebounceThrottle = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Rate Limiting</div>
                <h1>Rate Limiting: Debounce vs. Throttle</h1>
                <p className="hero-lead">
                    Master the industry-standard algorithms for controlling function execution
                    frequency. Learn to protect your server from search request floods and
                    ensure your UI remains performant during high-frequency scroll and resize events.
                </p>
            </div>

            <div className="content-section">
                <h2>The Strategy of Debouncing</h2>
                <p>
                    Debouncing ensures that a function is only triggered after a specified period
                    of "silence." It is the ideal architectural choice for **Search Inputs** or
                    **Window Resizing**, where you only care about the final state of the
                    user's action, not the intermediate steps.
                </p>
                <CodeBlock code={debounceCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Strategy of Throttling</h2>
                <p>
                    Throttling enforces a maximum execution frequency. If you throttle a function
                    to 100ms, it will execute at most once every 10th of a second. This is
                    critical for **Scroll Listeners**, **Mouse Tracking**, or **Game Loops**
                    where you need continuous feedback but want to avoid overwhelming the CPU.
                </p>
                <CodeBlock code={throttleCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Technical Breakdown: Which One to Choose?</h2>
                <p>
                    Choosing between the two depends on whether you need a response **immediately
                    during** the event or **after** the event has settled.
                </p>
                <CodeBlock code={visualizationCode} language="javascript" />
            </div>

            <ProTip><p>
                    Neither debounce nor throttle can fix a poorly optimized callback. If your
                    callback triggers a heavy "Reflow" (re-calculating layout), even at 100ms
                    intervals, the UI will stutter. Always combine rate-limiting with
                    efficient DOM manipulation techniques like `requestAnimationFrame`.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Context:** Use Debounce for auto-complete search to save API costs.</li>
                    <li>**Fluidity:** Use Throttle for scroll-parallax effects to maintain 60FPS.</li>
                    <li>**Precision:** For critical UI updates, use `requestAnimationFrame` instead of `setTimeout`.</li>
                    <li>**Libraries:** In production, prefer robust implementations from `lodash` or `underscore`.</li>
                    <li>**Accessibility:** Ensure that rate-limiting doesn't cause frustrating lag for screen readers.</li>
                    <li>**Cleanness:** Always cancel pending debounces when a component unmounts to avoid memory leaks.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSDebounceThrottle;


